import React from 'react';
import { motion } from 'framer-motion';
import { needs } from '../../data/needs';
import { brand } from '../../data/palette';
import type { NeedId } from '../../types/filtek';
interface NeedsOrbitProps {
  selected?: NeedId;
  explored: string[];
  matched?: NeedId;
  onSelect: (id: NeedId) => void;
}
const RADIUS = 37;
const angleFor = (index: number) => -90 + index * (360 / needs.length);

/** The same family logic as a rotating wheel of clinical demands. */
export function NeedsOrbit({
  selected,
  explored,
  matched,
  onSelect
}: NeedsOrbitProps) {
  const selectedIndex = needs.findIndex(need => need.id === selected);
  const active = selectedIndex >= 0 ? needs[selectedIndex] : undefined;
  return <div className="mx-auto w-full max-w-[540px] px-10 sm:px-14" data-id="element-82" data-mp="gysct0i52z8o">
      <div className="relative aspect-square w-full" data-id="element-83" data-mp="2eoa4pnstd5b4">
        <span className="absolute inset-[8%] rounded-full border border-enamel/10" aria-hidden="true" data-id="element-84" data-mp="2epli8fc29rff" />
        <motion.span className="absolute inset-[22%] rounded-full border border-dashed border-enamel/10" animate={{
        rotate: 360
      }} transition={{
        duration: 90,
        repeat: Infinity,
        ease: 'linear'
      }} aria-hidden="true" data-id="element-85" data-mp="2iryfsggnmj14" />

        {active && <motion.span className="absolute left-1/2 top-1/2 h-px origin-left" style={{
        width: `${RADIUS}%`,
        backgroundColor: active.accent
      }} initial={false} animate={{
        rotate: angleFor(selectedIndex),
        opacity: 0.55
      }} transition={{
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }} aria-hidden="true" data-id="element-86" data-mp="2tiq78taizptg" />}

        <div className="absolute inset-[26%] flex flex-col items-center justify-center text-center" data-id="element-87" data-mp="3c7d80zlo5t18">
          {active ? <motion.div key={active.id} initial={{
          opacity: 0,
          scale: 0.94
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          ease: [0.16, 1, 0.3, 1]
        }} data-id="element-88" data-mp="37c7m40s9oed5">
              <p className="label-mono text-[9px] text-mute" data-id="element-89" data-mp="nuzhz29584dm">When you need</p>
              <p className="mt-3 text-lg font-light leading-tight tracking-[-0.02em] sm:text-xl" style={{
            color: active.accent
          }} data-id="element-90" data-mp="rwkng2jclgds">
                {active.label}
              </p>
            </motion.div> : <motion.p className="label-mono text-[9px] text-mute" animate={{
          opacity: [0.45, 1, 0.45]
        }} transition={{
          duration: 3.2,
          repeat: Infinity
        }} data-id="element-91" data-mp="iy92oh55xct9">
              Choose a need
            </motion.p>}
        </div>

        {needs.map((need, index) => {
        const angle = angleFor(index) * Math.PI / 180;
        const left = 50 + RADIUS * Math.cos(angle);
        const top = 50 + RADIUS * Math.sin(angle);
        const isSelected = selected === need.id;
        const isExplored = explored.includes(need.id);
        return <button key={need.id} type="button" onClick={() => onSelect(need.id)} aria-pressed={isSelected} className="group absolute flex w-28 -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-2.5 sm:w-32" style={{
          left: `${left}%`,
          top: `${top}%`
        }} data-id="element-92" data-mp="32r36h9j7j3or" data-mpi={index}>
              <span className="relative flex h-6 w-6 items-center justify-center" data-id="element-93" data-mp="37ol6gaf8y8km">
                <motion.span className="absolute rounded-full" style={{
              backgroundColor: need.accent
            }} initial={false} animate={{
              width: isSelected ? 13 : 8,
              height: isSelected ? 13 : 8,
              opacity: isSelected ? 1 : isExplored ? 0.8 : 0.3
            }} transition={{
              duration: 0.45
            }} data-id="element-94" data-mp="3lovznk413z8y" />
                {isSelected && <motion.span className="absolute h-4 w-4 rounded-full" style={{
              border: `1px solid ${need.accent}`
            }} animate={{
              scale: [1, 2.3],
              opacity: [0.7, 0]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeOut'
            }} data-id="element-95" data-mp="4aigfdkj8opb" />}
              </span>

              <span className="text-center text-[11px] font-light leading-tight transition-colors duration-500 sm:text-sm" style={{
            color: isSelected ? need.accent : isExplored ? 'var(--enamel)' : brand.dim
          }} data-id="element-96" data-mp="2cgt1914vnpkd">
                {need.label}
              </span>

              {matched === need.id && !isSelected && <span className="label-mono text-[8px] text-mute/70" data-id="element-97" data-mp="1tqbbn5fcda38">Matches you</span>}
            </button>;
      })}
      </div>
    </div>;
}